import { Directive, forwardRef, HostBinding, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslationService } from 'app/core/internationalization/translation.service';
import { KH_A11Y_TARGET } from '../constants/a11y-target.token';
import { A11yBooleanInput } from '../interfaces/a11y.interfaces';
import { toBoolean } from '../utils/a11y.util';

import { A11yLabelDirective } from './a11y-label.directive';

@Directive({
  selector: '[khA11yStepIndicator]',
  providers: [{ provide: KH_A11Y_TARGET, useExisting: forwardRef(() => A11yStepIndicatorDirective) }]
})
export class A11yStepIndicatorDirective extends A11yLabelDirective {
  private readonly _translationService = inject(TranslationService);

  private _defaultStepLabel: string | null = this.defaultStepLabel();

  public constructor() {
    super();
    this._translationService.translationsReady$
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this._defaultStepLabel = this._translationService.instant('A11Y_STEP_INDICATOR.STEP_COUNT', { currentStep: this.currentStep, totalStepCount: this.totalStepCount }) || this._defaultStepLabel;
      });
  }

  @Input() public khA11yStepIndicator: number | string | null = null;
  @Input() public khA11yStepIndicatorTotal: number | string | null = null;
  @Input() public khA11yStepIndicatorCurrent: A11yBooleanInput = false;

  @HostBinding('attr.role') public readonly role = 'listitem';

  protected override get ariaLabelValue(): string | null {
    if (this.a11yLabel) {
      return this.a11yLabel;
    }

    return this._defaultStepLabel;
  }

  @HostBinding('attr.aria-posinset')
  public get ariaPositionInSet(): string | null {
    return this.currentStep?.toString() ?? null;
  }

  @HostBinding('attr.aria-setsize')
  public get ariaSetSize(): string | null {
    return this.totalStepCount?.toString() ?? null;
  }

  @HostBinding('attr.aria-current')
  public get ariaCurrent(): 'step' | null {
    return toBoolean(this.khA11yStepIndicatorCurrent) ? 'step' : null;
  }

  private get currentStep(): number | null {
    const zeroBasedIndex = this.toNonNegativeInteger(this.khA11yStepIndicator);

    return zeroBasedIndex === null ? null : zeroBasedIndex + 1;
  }

  private get totalStepCount(): number | null {
    return this.toPositiveInteger(this.khA11yStepIndicatorTotal);
  }

  private toNonNegativeInteger(value: number | string | null): number | null {
    const parsedValue = this.toInteger(value);

    return parsedValue !== null && parsedValue >= 0 ? parsedValue : null;
  }

  private toPositiveInteger(value: number | string | null): number | null {
    const parsedValue = this.toInteger(value);

    return parsedValue !== null && parsedValue > 0 ? parsedValue : null;
  }

  private toInteger(value: number | string | null): number | null {
    if (value === null || value === '') {
      return null;
    }

    const numericValue = Number(value);

    return Number.isInteger(numericValue) ? numericValue : null;
  }

  private defaultStepLabel(): string | null {
    if (this.currentStep === null || this.totalStepCount === null) {
      return null;
    }

    return `Step ${this.currentStep.toString()} of ${this.totalStepCount.toString()}`;
  }
}
